import { useOne, useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useSession } from "hooks/useSession";
import { EyeIcon, UsersThreeIcon } from "@phosphor-icons/react";
import styled, { keyframes } from "styled-components";
import { useOrganizationTotals } from "../hooks/useOrganizationAnalytics";
import { MediaProjectResponse } from "../../types";
import { radius } from "@libertify/ui";

const formatCompactNumber = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
};

export const AnalyticsCards = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { currentOrganization } = useSession();

  const { totals, isLoading: totalsLoading } = useOrganizationTotals(
    currentOrganization?.id
  );

  const mostViewedProject = totals?.most_viewed_project ?? undefined;
  const mostViewedCount = mostViewedProject?.impression_sessions ?? null;

  // The totals endpoint only returns project_id — fetch the title separately.
  const { data: mostViewedDetails } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: mostViewedProject?.project_id ?? "",
    queryOptions: {
      enabled: Boolean(mostViewedProject?.project_id),
    },
    errorNotification: false,
  });

  const mostViewedTitle = mostViewedDetails?.data.title ?? null;

  const totalEngagement = totals
    ? totals.total_impression_sessions +
      totals.total_document_sessions +
      totals.total_chat_sessions
    : null;

  const goToProject = () => {
    if (mostViewedProject?.project_id && mostViewedDetails?.data) {
      navigate(
        `/media/projects/${mostViewedProject.project_id}/analytics?period=all&dateType=30D`
      );
    } else {
      navigate("/media/analytics");
    }
  };

  // Avoid render flicker: don't show anything until we know there's data to display.
  // (Skeletons would briefly flash and then disappear for orgs with no analytics.)
  if (totalsLoading) return null;

  // Hide entirely when there are no views and all session counts (video, doc, chat) are 0.
  const hasData = (mostViewedCount ?? 0) > 0 || (totalEngagement ?? 0) > 0;
  if (!hasData) return null;

  return (
    <>
      {/* Card: Most viewed experience */}
      <Card $delay={200}>
        <CardHeader>
          <IconBadge>
            <EyeIcon size={14} weight="bold" />
          </IconBadge>
          <Label>{t("pages.home.index.mostViewedExperience")}</Label>
        </CardHeader>

        <StatBlock>
          <ViewCount>
            <StatValue>{formatCompactNumber(mostViewedCount)}</StatValue>
            <Unit>{t("pages.home.index.views")}</Unit>
          </ViewCount>
          {mostViewedTitle && (
            <ProjectTitle title={mostViewedTitle}>
              {mostViewedTitle}
            </ProjectTitle>
          )}
        </StatBlock>

        <ViewAllLink onClick={goToProject}>
          {t("pages.home.index.viewAll")} →
        </ViewAllLink>
      </Card>

      {/* Card: Total engagement */}
      <Card $delay={380}>
        <CardHeader>
          <IconBadge $accent>
            <UsersThreeIcon size={14} weight="bold" />
          </IconBadge>
          <Label>{t("pages.home.index.totalEngagement")}</Label>
        </CardHeader>

        <StatBlock>
          <ViewCount>
            <StatValue>{formatCompactNumber(totalEngagement)}</StatValue>
            <Unit>{t("pages.home.index.totalSessions")}</Unit>
          </ViewCount>
          <Subtext>
            {totals
              ? t("pages.home.index.engagementBreakdown", {
                  video: formatCompactNumber(totals.total_impression_sessions),
                  document: formatCompactNumber(totals.total_document_sessions),
                  chat: formatCompactNumber(totals.total_chat_sessions),
                })
              : t("pages.home.index.acrossAllExperiences")}
          </Subtext>
        </StatBlock>
      </Card>
    </>
  );
};

/* ── styles ─────────────────────────────────────────────────────────────── */

const reveal = keyframes`
  0% {
    opacity: 0;
    width: 0;
    padding-left: 0;
    padding-right: 0;
    transform: translateY(8px) scale(0.96);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    width: 300px;
    padding-left: 24px;
    padding-right: 24px;
    transform: translateY(0) scale(1);
  }
`;

const revealMobile = keyframes`
  0% {
    opacity: 0;
    width: 0;
    padding-left: 0;
    padding-right: 0;
    transform: translateY(8px) scale(0.96);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    width: 255px;
    padding-left: 20px;
    padding-right: 20px;
    transform: translateY(0) scale(1);
  }
`;

const Card = styled.div<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-radius: ${radius.box}px;
  width: 300px;
  flex-shrink: 0;
  height: 228px;
  background-color: ${({ theme }) => theme.colorBgElevated};
  border: 1.5px solid ${({ theme }) => theme.colorBorderSecondary};
  text-align: left;
  font-family: "Inter", sans-serif;
  overflow: hidden;
  animation: ${reveal} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: ${({ $delay = 0 }) => `${$delay}ms`};

  @media (width <= 768px) {
    width: 255px;
    height: 194px;
    padding: 20px;
    gap: 10px;
    animation-name: ${revealMobile};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBadge = styled.div<{ $accent?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background-color: ${({ $accent }) => ($accent ? "#FFF4D6" : "#ECFFFE")};
  color: ${({ $accent }) => ($accent ? "#A06600" : "#247F79")};
`;

const Label = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #090e19;
`;

const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  justify-content: center;
`;

const ViewCount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const StatValue = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -1px;
  color: #090e19;
  line-height: 1;
`;

const Unit = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #090e19;
`;

const ProjectTitle = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #090e19;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtext = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colorPrimary};
  cursor: pointer;
  align-self: flex-end;
  margin-top: auto;

  &:hover {
    text-decoration: underline;
  }
`;
